import { Injectable, inject } from '@angular/core';
import { catchError, finalize, switchMap, take, tap } from 'rxjs/operators';
import { iif, Observable, of } from 'rxjs';

import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';

import { OfficeService } from '@pages/office/services/office.service';
import { OfficeToolsEvents } from '@pages/office/models/office-tools-events.model';
import { commonCatchErrorHandler } from '@pages/office/office.constants';
import { ComponentProperties } from '@core/types/component-properties.type';
import { NotificationsService } from '@core/services/notifications.service';
import { UtilityService } from '@core/services/utility.service';
import { IModalFormMetadataModel } from '../../models/modal-form-metadata.model';
import { FormEventsModalComponent } from './form-events-modal/form-events-modal.component';
import { FormEventsModalConfig } from './models/form-events-modal-config.model';

@Injectable({ providedIn: 'root' })
export class OfficeToolsEventsService {
  public formEvents = new Set<OfficeToolsEvents>([]);

  private modalService = inject(BsModalService);
  private officeService = inject(OfficeService);
  private notificationsService = inject(NotificationsService);
  private utilityService = inject(UtilityService);
  private commonCatchErrorHandler = commonCatchErrorHandler();

  public preEventsBridge(config: FormEventsModalConfig): Observable<string> {
    return iif(() => this.formEvents.has(config.eventName), this.showModalForm(config), of(null));
  }

  private showModalForm(config: FormEventsModalConfig): Observable<string> {
    this.utilityService.openModal();
    return this.officeService.getModalMetadata(config).pipe(
      switchMap(form =>
        iif(
          () => !!form,
          of(form).pipe(
            switchMap(form => {
              this.utilityService.closeModal();
              return this.showModal(form).content.onConfirm.pipe(
                take(1),
                switchMap(modalFormData => {
                  modalFormData && this.utilityService.openModal();
                  return iif(
                    () => !!modalFormData,
                    this.officeService
                      .postModalForm(config, modalFormData)
                      .pipe(tap(message => message && this.notificationsService.showSuccess(message))),
                    of(null)
                  );
                })
              );
            })
          ),
          of(null)
        )
      ),
      catchError(this.commonCatchErrorHandler),
      finalize(() => this.utilityService.closeModal())
    );
  }

  private showModal(model: IModalFormMetadataModel): BsModalRef<FormEventsModalComponent> {
    const initialState: ComponentProperties<Partial<FormEventsModalComponent>> = {
      title: model.title,
      officeFields: model.items,
    };
    const config: ModalOptions = {
      initialState,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
    };
    return this.modalService.show(FormEventsModalComponent, config);
  }
}
